import React, { useRef, useEffect, useState } from 'react';
import { Box, Button, Grid, Tab, Alert, Typography, styled, useTheme, alpha } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RatesInput from './RatesInput';
import Divider from '../../components/Divider';
import GetRatesAction from './GetRatesAction';
import GetRatesWrapper from '../../context/GetRates/Provider';
import { GetRatesContextType } from './Type';
import GetRatesContext from '../../context/GetRates';
import CSPList from './CSPList';
import NoCSPFound from './CSPList/NoCSPFound/NoCSPFound';
import BoxWise from './BoxWise';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '../../store';
import Loader from '../../components/Loader';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RatesInputType } from './Type';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import AccountLookup from '@Components/AccountLookup';
import SelectedUserInfoCard from '@Components/AccountLookup/SelectedUserInfoCard';
import Title from '@Components/Title';
import Image from '@Components/Image';
import { UserObj } from '@Reducers/AccountLookup/Type';
import { userLogin } from '../../services/hasAdminAccess';
import useAccountLookup from '@Hook/useAccountLookup';
import { getUserList } from '@Reducers/AccountLookup';
import { getRates, resetCharges, setFormData } from '../../store/reducers/GetRates';
import { getDefaultRates } from '../../store/reducers/GetRates/actions';
import { getAllSavedAddress } from '../../store/reducers/SavedAddress/actions';
import { AddressType as SavedAddressType } from '../../store/reducers/SavedAddress/Type';

// Single shipment AI services & modal
import ImportAiModal from '../CreateShipment/UserDetails/ImportAiModal';
import { getUploadUrl, uploadToS3 } from '../CreateShipment/UserDetails/services/uploadService';
import { convertExcelToCsv } from '../CreateShipment/UserDetails/services/excelParser';
import {
  isExcelFile,
  mapAiResponseToGetRates,
  buildRatesPayload,
  extractShipmentDocumentAsync,
  extractShipmentTextAsync,
  pollShipmentExtraction,
  formatInvalidFieldsReason,
} from './services/aiRatesHelper';

type TabValues = 'tarrif_pricing' | 'contract_pricing' | string;

interface TabAccessKeyType {
  isCompanyInfoShow: boolean;
}

interface GetRatesContentType {
  hideBoxes: boolean;
  ratesInput: RatesInputType;
  isTarrifPricing?: boolean;
  pickupSuggestions?: SavedAddressType[];
  destinationSuggestions?: SavedAddressType[];
  savedAddressesLoading?: boolean;
  savedAddressesError?: any;
}

interface ContractPricing {
  hideBoxes: boolean;
  ratesInput: RatesInputType;
  isTarrifPricing?: boolean;
  modalId: string;
  pickupSuggestions?: SavedAddressType[];
  destinationSuggestions?: SavedAddressType[];
  savedAddressesLoading?: boolean;
  savedAddressesError?: any;
}

type TabAccessKey = keyof TabAccessKeyType;

const TABS: { label: string; value: TabValues; accessKey?: TabAccessKey }[] = [
  { label: 'Tarrif Pricing', value: 'tarrif_pricing' },
  { label: 'Contract Pricing ', value: 'contract_pricing' },
];

const TAB_KEYS: TabValues[] = TABS.map(x => x.value);

const Root = styled(Grid)(({ theme }) => ({
  scrollMargin: '95px',
  [theme.breakpoints.down('sm')]: {
    padding: 0,
  },
}));

export function GetRatesContent({
  hideBoxes,
  ratesInput,
  isTarrifPricing = false,
  pickupSuggestions,
  destinationSuggestions,
  savedAddressesLoading,
  savedAddressesError,
}: GetRatesContentType) {
  return (
    <>
      <RatesInput
        pickupSuggestions={pickupSuggestions}
        destinationSuggestions={destinationSuggestions}
        savedAddressesLoading={savedAddressesLoading}
        savedAddressesError={savedAddressesError}
      />
      {!hideBoxes && ratesInput?.shipmentType === 'parcel' && <BoxWise />}
      <GetRatesAction isTarrifPricing={isTarrifPricing} />
    </>
  );
}

function ContractPricing({
  hideBoxes,
  ratesInput,
  modalId,
  pickupSuggestions,
  destinationSuggestions,
  savedAddressesLoading,
  savedAddressesError,
}: ContractPricing) {
  const { selectedUser } = useAccountLookup(modalId);
  const { setUserId } = React.useContext(GetRatesContext) as GetRatesContextType;
  const dispatch = useDispatch<AppDispatch>();

  function getSelectedUser(user: UserObj) {
    setUserId(user._id);
  }

  useEffect(() => {
    if (modalId) {
      dispatch(getUserList([modalId, {}]));
    }
  }, [modalId]);

  return (
    <>
      <div className="border-1px border-light rounded-md mb-3 mr-3" style={{ padding: '1.25rem' }}>
        <Grid container>
          <Grid item xs={12} sm={6} md={5}>
            <AccountLookup modalId={modalId} getSelectedUser={getSelectedUser} showSearchInput disableUserReset>
              <Title
                titleIconLeft={<Image src="/images/icons/contract_pricing.svg" alt="contract_pricing" />}
                title="Contract Pricing "
                removeBorder
              />
            </AccountLookup>
          </Grid>
        </Grid>
        {selectedUser?._id && (
          <>
            <Divider sx={{ my: 3 }} />
            <SelectedUserInfoCard user={selectedUser} />
          </>
        )}
      </div>
      {selectedUser?._id && (
        <GetRatesContent
          hideBoxes={hideBoxes}
          ratesInput={ratesInput}
          pickupSuggestions={pickupSuggestions}
          destinationSuggestions={destinationSuggestions}
          savedAddressesLoading={savedAddressesLoading}
          savedAddressesError={savedAddressesError}
        />
      )}
    </>
  );
}

function GetRatesV2Main() {
  const modalId = 'GetRatesV2Main';
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();
  const { rateFor, loading, CSPChargesData, formData } = useSelector((state: RootStateType) => state.getRates);
  const { token } = useSelector((state: RootStateType) => state.loginUser);
  const loggedInUserId = useSelector((state: RootStateType) => state.loginUser.profile?._id);
  const { countries, countryList } = useSelector((state: RootStateType) => state.global);
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};

  const {
    shipperAddresses = [],
    consigneeAddresses = [],
    savedAddressList = [],
    loading: savedAddressesLoading,
    error: savedAddressesError,
  } = useSelector((state: RootStateType) => state.savedAddress);

  const savedAddressFetchedRef = useRef(false);
  useEffect(() => {
    if (!savedAddressFetchedRef.current) {
      savedAddressFetchedRef.current = true;
      dispatch(getAllSavedAddress());
    }
  }, [dispatch]);

  const pickupSuggestions = React.useMemo(() => {
    if (shipperAddresses.length > 0) return shipperAddresses;
    return savedAddressList.filter(addr => addr.type === 'shipper');
  }, [shipperAddresses, savedAddressList]);

  const destinationSuggestions = React.useMemo(() => {
    if (consigneeAddresses.length > 0) return consigneeAddresses;
    return savedAddressList.filter(addr => addr.type === 'consignee');
  }, [consigneeAddresses, savedAddressList]);

  const { selectedUser, loading: accountLookupLoading } = useAccountLookup(modalId, true);
  const {
    hideBoxes,
    showRates,
    ratesInput,
    setShowRates,
    setUserId,
    setPickup,
    setDestination,
    setBoxes,
    setBoxCount,
    setHideBoxes,
    setRateInput,
    setChargeableWeight,
    boxes,
    pickup,
    destination,
  } = React.useContext(GetRatesContext) as GetRatesContextType;

  const ref: React.RefObject<HTMLDivElement> = useRef(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();

  const [currentTap, setCurrentTap] = useState<TabValues>('tarrif_pricing');

  // AI states
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [extractionError, setExtractionError] = useState<string | null>(null);
  const [aiBanner, setAiBanner] = useState<{
    show: boolean;
    text: string;
    severity: 'info' | 'warning' | 'success';
  }>({ show: false, text: '', severity: 'info' });
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isCancelledRef = useRef<boolean>(false);

  // Clean up any pending polling timer on unmount
  useEffect(() => {
    return () => {
      isCancelledRef.current = true;
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, []);

  // Reset cancellation flag when modal opens, cancel if modal closes
  useEffect(() => {
    if (isModalOpen) {
      isCancelledRef.current = false;
    } else {
      isCancelledRef.current = true;
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    }
  }, [isModalOpen]);

  const handleCancelAndCloseModal = () => {
    isCancelledRef.current = true;
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    setIsImporting(false);
    setIsModalOpen(false);
  };

  // Auto-dismiss success notification banner
  useEffect(() => {
    if (aiBanner.show && aiBanner.severity === 'success') {
      const timer = setTimeout(() => {
        setAiBanner({ show: false, text: '', severity: 'info' });
      }, 4500);
      return () => clearTimeout(timer);
    }
  }, [aiBanner.show, aiBanner.severity]);

  const isAdmin = !userLogin();

  useEffect(() => {
    if (!isAdmin && loggedInUserId) {
      setUserId(loggedInUserId);
    }
  }, [isAdmin, loggedInUserId]);

  useEffect(() => {
    const currentActiveTab: TabValues = searchParams.get('tab') || 'tarrif_pricing';
    if (TAB_KEYS.includes(currentActiveTab)) setCurrentTap(currentActiveTab);
  }, []);

  useEffect(() => {
    ref.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [showRates]);

  function handleBookCSP() {
    if (rateFor === 'get-rates') {
      dispatch(
        setFormData({
          ...ratesInput,
          pickup,
          destination,
          boxes,
          hideBoxes,
          currency: (formData as any)?.currency || '',
          userReferenceNumber: (formData as any)?.userReferenceNumber || '',
          shipmentMode: (formData as any)?.shipmentMode || '',
          aiFields: (formData as any)?.aiFields,
        }),
      );
      const url = selectedUser?._id
        ? `/shipment/create-single?accountId=${selectedUser?._id}&redirectedFrom=GetRates`
        : '/shipment/create-single?redirectedFrom=GetRates';
      navigate(url);
    }
  }

  const onTabChange = (_e: React.SyntheticEvent, currentTab: TabValues) => {
    navigate(`?tab=${currentTab}`);
    setCurrentTap(currentTab);
    dispatch(resetCharges());
    setAiBanner({ show: false, text: '', severity: 'info' });
  };

  /**
   * Process extracted fields from AI: map to form context and trigger rates if valid
   */
  const processAiFields = (fields: any, reviewMessage?: string) => {
    const mapped = mapAiResponseToGetRates(fields, countries, countryList);

    const updatedRatesInput: RatesInputType = {
      shipmentType: mapped.shipmentType,
      pickup: mapped.pickup,
      destination: mapped.destination,
      pickupPinCode: mapped.pickupPinCode,
      destinationPinCode: mapped.destinationPinCode,
      weight: mapped.weight,
      weightUnit: mapped.weightUnit,
    };

    setPickup(mapped.pickup);
    setDestination(mapped.destination);
    setBoxes(mapped.boxes);
    setBoxCount(mapped.boxCount);
    setHideBoxes(mapped.hideBoxes);
    setRateInput(updatedRatesInput);
    setChargeableWeight(mapped.chargeableWeight);

    // Update Redux state directly with the fresh mapped data
    dispatch(
      setFormData({
        ...updatedRatesInput,
        boxes: mapped.boxes,
        hideBoxes: mapped.hideBoxes,
        currency: fields?.package?.currency || '',
        userReferenceNumber: fields?.userReferenceNumber || '',
        shipmentMode: fields?.shipmentMode || '',
        aiFields: fields,
      }),
    );

    const isTarrif = currentTap === 'tarrif_pricing';

    // If there's an explicit review requirement from AI extraction, surface it prominently
    if (reviewMessage) {
      setAiBanner({
        show: true,
        text: `✨ Shipment details prefilled with review note: ${reviewMessage}`,
        severity: 'warning',
      });
      return;
    }

    if (mapped.isValid) {
      // Check if we can automatically trigger rates
      if (isTarrif || !isAdmin) {
        const payload = buildRatesPayload(mapped, isTarrif, undefined, name);
        if (token?._id) {
          dispatch(getRates([payload, isTarrif]));
        } else {
          dispatch(getDefaultRates([payload, isTarrif]));
        }
        setAiBanner({
          show: true,
          text: '✨ Shipment details extracted & rates calculated successfully!',
          severity: 'success',
        });
      } else {
        // Contract pricing tab
        if (selectedUser?._id) {
          const payload = buildRatesPayload(mapped, false, selectedUser._id, name);
          dispatch(getRates([payload, false]));
          setAiBanner({
            show: true,
            text: '✨ Shipment details extracted & contract rates calculated successfully!',
            severity: 'success',
          });
        } else {
          setAiBanner({
            show: true,
            text: '✨ Shipment details prefilled! Please select a customer account above to calculate contract rates.',
            severity: 'info',
          });
        }
      }
    } else {
      setAiBanner({
        show: true,
        text: `✨ Shipment details prefilled. Please fill missing required fields (${mapped.missingFields.join(
          ', ',
        )}) to calculate rates.`,
        severity: 'warning',
      });
    }
  };

  /**
   * Handle text-based extraction
   */
  const performTextExtraction = async (text: string) => {
    if (!text.trim()) {
      setExtractionError('Please enter some shipment details.');
      return;
    }
    if (isImporting) return;

    isCancelledRef.current = false;
    setIsImporting(true);
    setExtractionError(null);
    try {
      const initialResponse = await extractShipmentTextAsync(text);
      if (isCancelledRef.current) return;

      const response = await pollShipmentExtraction(initialResponse, {
        isCancelled: () => isCancelledRef.current,
        registerTimer: timer => {
          pollingTimerRef.current = timer;
        },
      });

      if (isCancelledRef.current) return;

      if (response.status === 'completed') {
        if (response.fields && Object.keys(response.fields).length > 0) {
          processAiFields(response.fields);
          setIsModalOpen(false);
        } else {
          console.error('❌ Unexpected text extraction response:', response);
          setExtractionError('The extraction service did not return any fields.');
        }
      } else if (response.status === 'needs_review') {
        const reviewReason = formatInvalidFieldsReason(response.invalidFields) || response.error || '';
        if (response.fields && Object.keys(response.fields).length > 0) {
          processAiFields(response.fields, reviewReason);
          setIsModalOpen(false);
        } else {
          setExtractionError(
            reviewReason || response.error || 'Document extraction requires review. Please enter details manually.',
          );
        }
      } else if (response.status === 'failed' || response.error) {
        setExtractionError(response.error || 'Failed to extract text details.');
      } else {
        setExtractionError(response.error || `Unexpected extraction status: ${response.status}`);
      }
    } catch (error: any) {
      if (isCancelledRef.current) return;
      console.error('❌ Text Extraction Failed:', error);
      setExtractionError(error.message || 'Failed to extract text details.');
    } finally {
      if (!isCancelledRef.current) {
        setIsImporting(false);
      }
    }
  };

  /**
   * Handle single file upload extraction (PDF, images, Excel, Word, etc.)
   */
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;

    const selectedFiles = Array.from(fileList);
    event.target.value = '';

    if (selectedFiles.length > 1) {
      setExtractionError('Please upload 1 file at a time for rate estimation.');
      return;
    }
    if (isImporting) return;

    const file = selectedFiles[0];
    isCancelledRef.current = false;
    setIsImporting(true);
    setExtractionError(null);

    try {
      if (isExcelFile(file)) {
        try {
          const csvContent = await convertExcelToCsv(file);
          if (isCancelledRef.current) return;
          if (!csvContent) {
            setExtractionError('The uploaded Excel file does not contain any usable data.');
            setIsImporting(false);
            return;
          }

          const initialResponse = await extractShipmentTextAsync(csvContent);
          if (isCancelledRef.current) return;

          const response = await pollShipmentExtraction(initialResponse, {
            isCancelled: () => isCancelledRef.current,
            registerTimer: timer => {
              pollingTimerRef.current = timer;
            },
          });

          if (isCancelledRef.current) return;

          if (response.status === 'completed') {
            if (response.fields && Object.keys(response.fields).length > 0) {
              processAiFields(response.fields);
              setIsModalOpen(false);
            } else {
              console.error('❌ Unexpected Excel extraction response:', response);
              setExtractionError('The extraction service did not return any fields.');
            }
          } else if (response.status === 'needs_review') {
            const reviewReason = formatInvalidFieldsReason(response.invalidFields) || response.error || '';
            if (response.fields && Object.keys(response.fields).length > 0) {
              processAiFields(response.fields, reviewReason);
              setIsModalOpen(false);
            } else {
              setExtractionError(
                reviewReason || response.error || 'Excel extraction requires review. Please enter details manually.',
              );
            }
          } else if (response.status === 'failed' || response.error) {
            setExtractionError(response.error || 'Failed to extract Excel document details.');
          } else {
            setExtractionError(response.error || `Unexpected extraction status: ${response.status}`);
          }
        } catch (error: any) {
          if (isCancelledRef.current) return;
          console.error('❌ Excel Extraction Failed:', error);
          setExtractionError(error.message || 'Failed to extract Excel document details.');
        }
      } else {
        // Step 1: Request Upload URL
        let uploadUrlResponse;
        try {
          uploadUrlResponse = await getUploadUrl(file);
        } catch (error: any) {
          if (isCancelledRef.current) return;
          console.error('❌ Upload URL Request Failed:', error);
          setExtractionError(error.message || 'Failed to request upload URL.');
          setIsImporting(false);
          return;
        }

        if (isCancelledRef.current) return;

        if (!uploadUrlResponse || !uploadUrlResponse.url || !uploadUrlResponse.fields || !uploadUrlResponse.key) {
          console.error('❌ Unexpected upload URL response:', uploadUrlResponse);
          setExtractionError('The upload URL response is incomplete or invalid.');
          setIsImporting(false);
          return;
        }

        // Step 2: Upload File to S3
        try {
          await uploadToS3(uploadUrlResponse, file);
        } catch (error: any) {
          if (isCancelledRef.current) return;
          console.error('❌ S3 Upload Failed:', error);
          setExtractionError(error.message || 'Failed to upload file to S3.');
          setIsImporting(false);
          return;
        }

        if (isCancelledRef.current) return;

        // Step 3: Call Async Extraction API and Poll
        try {
          const initialResponse = await extractShipmentDocumentAsync(uploadUrlResponse.key);
          if (isCancelledRef.current) return;

          const response = await pollShipmentExtraction(initialResponse, {
            isCancelled: () => isCancelledRef.current,
            registerTimer: timer => {
              pollingTimerRef.current = timer;
            },
          });

          if (isCancelledRef.current) return;

          if (response.status === 'completed') {
            if (response.fields && Object.keys(response.fields).length > 0) {
              processAiFields(response.fields);
              setIsModalOpen(false);
            } else {
              console.error('❌ Unexpected extraction response:', response);
              setExtractionError('The extraction service did not return any fields.');
            }
          } else if (response.status === 'needs_review') {
            const reviewReason = formatInvalidFieldsReason(response.invalidFields) || response.error || '';
            if (response.fields && Object.keys(response.fields).length > 0) {
              processAiFields(response.fields, reviewReason);
              setIsModalOpen(false);
            } else {
              setExtractionError(
                reviewReason || response.error || 'Document extraction requires review. Please enter details manually.',
              );
            }
          } else if (response.status === 'failed' || response.error) {
            setExtractionError(response.error || 'Failed to extract document details.');
          } else {
            setExtractionError(response.error || `Unexpected extraction status: ${response.status}`);
          }
        } catch (error: any) {
          if (isCancelledRef.current) return;
          console.error('❌ Extraction Failed:', error);
          setExtractionError(error.message || 'Failed to extract document details.');
        }
      }
    } catch (error: any) {
      if (isCancelledRef.current) return;
      console.error('❌ AI Extraction error:', error);
      setExtractionError(error.message || 'Failed to extract shipment details.');
    } finally {
      if (!isCancelledRef.current) {
        setIsImporting(false);
      }
    }
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        accept="application/pdf,image/png,image/jpeg,image/jpg,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <ImportAiModal
        open={isModalOpen}
        onClose={handleCancelAndCloseModal}
        isImporting={isImporting}
        onUploadClick={() => fileInputRef.current?.click()}
        onExtractText={performTextExtraction}
        error={extractionError}
        setError={setExtractionError}
        hideSampleExcel
      />

      <Loader loading={loading || accountLookupLoading} overly>
        <Root ref={ref} container className={!loggedInUserId ? '' : 'bg-white rounded'}>
          <Grid
            item
            xs={12}
            className="mb-1"
            sx={{ padding: isAdmin ? 'none' : { xs: '3rem 3rem 0 1.5rem', sm: '3rem 3rem 0 3rem' } }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                width: '100%',
                pb: 1,
              }}
            >
              <Box>
                <Title title="Get Rates" removeBorder />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mt: 0.25,
                    fontSize: '0.875rem',
                    fontWeight: 400,
                  }}
                >
                  Quickly calculate, estimate, and compare real-time shipping rates
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={() => {
                  setIsModalOpen(true);
                  setExtractionError(null);
                }}
                startIcon={<AutoAwesomeIcon sx={{ fontSize: '18px !important' }} />}
                sx={{
                  borderRadius: '24px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  px: 2.75,
                  py: 1,
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText || '#fff',
                  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.25)}`,
                  alignSelf: { xs: 'stretch', sm: 'center' },
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark,
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.35)}`,
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                Import with AI
              </Button>
            </Box>
          </Grid>

          {aiBanner.show && (
            <Grid
              item
              xs={12}
              sx={{
                px: isAdmin ? 0 : { xs: '1.5rem', sm: '3rem' },
                mt: 1,
                mb: 2,
                width: '100%',
              }}
            >
              <Alert
                severity={aiBanner.severity}
                onClose={() => setAiBanner({ show: false, text: '', severity: 'info' })}
                sx={{
                  borderRadius: '12px',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}
              >
                {aiBanner.text}
              </Alert>
            </Grid>
          )}

          {CSPChargesData?.charges?.length && rateFor === 'get-rates' ? (
            <>
              {selectedUser?._id && currentTap != 'tarrif_pricing' && (
                <SelectedUserInfoCard user={selectedUser} classname="mb-2" />
              )}
              <CSPList weightUnit={ratesInput.weightUnit} handleBookCSP={handleBookCSP} />
            </>
          ) : (
            <Grid container item>
              {isAdmin && loggedInUserId ? (
                <TabContext value={currentTap}>
                  <Grid
                    container
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      marginBottom: '28px',
                      px: { xs: 0, sm: 0 },
                    }}
                  >
                    <TabList
                      onChange={onTabChange}
                      indicatorColor="primary"
                      TabIndicatorProps={{
                        sx: {
                          height: '3px',
                          borderRadius: '3px 3px 0 0',
                          backgroundColor: theme.palette.primary.main,
                        },
                      }}
                      scrollButtons="auto"
                      variant="scrollable"
                    >
                      {TABS.map(cTab => (
                        <Tab
                          sx={{
                            textTransform: 'none',
                            padding: '14px 20px',
                            fontWeight: currentTap === cTab.value ? 600 : 500,
                            fontSize: '0.95rem',
                            color: currentTap === cTab.value ? theme.palette.primary.main : 'text.secondary',
                            '&:hover': {
                              color: theme.palette.primary.main,
                            },
                          }}
                          value={cTab.value}
                          label={cTab.label}
                          key={cTab.value}
                        />
                      ))}
                    </TabList>
                  </Grid>
                  <TabPanel value="tarrif_pricing" className="p-0 w-full">
                    <GetRatesContent
                      hideBoxes={hideBoxes}
                      ratesInput={ratesInput}
                      isTarrifPricing={true}
                      pickupSuggestions={pickupSuggestions}
                      destinationSuggestions={destinationSuggestions}
                      savedAddressesLoading={savedAddressesLoading}
                      savedAddressesError={savedAddressesError}
                    />
                  </TabPanel>
                  <TabPanel value="contract_pricing" className="p-0 w-full">
                    <ContractPricing
                      modalId={modalId}
                      hideBoxes={hideBoxes}
                      ratesInput={ratesInput}
                      pickupSuggestions={pickupSuggestions}
                      destinationSuggestions={destinationSuggestions}
                      savedAddressesLoading={savedAddressesLoading}
                      savedAddressesError={savedAddressesError}
                    />
                  </TabPanel>
                </TabContext>
              ) : (
                <Box sx={{ padding: { xs: '0 1.5rem', sm: '0 3rem' }, width: '100%' }}>
                  {CSPChargesData?.charges?.length && rateFor === 'get-rates' ? (
                    <>
                      <CSPList weightUnit={ratesInput.weightUnit} handleBookCSP={handleBookCSP} />
                    </>
                  ) : (
                    <GetRatesContent
                      hideBoxes={hideBoxes}
                      ratesInput={ratesInput}
                      pickupSuggestions={pickupSuggestions}
                      destinationSuggestions={destinationSuggestions}
                      savedAddressesLoading={savedAddressesLoading}
                      savedAddressesError={savedAddressesError}
                    />
                  )}
                </Box>
              )}
            </Grid>
          )}
          <NoCSPFound setShowRates={setShowRates} />
        </Root>
      </Loader>
    </>
  );
}

const GetRatesV2: React.FC = (): JSX.Element => {
  return (
    <GetRatesWrapper>
      <GetRatesV2Main />
    </GetRatesWrapper>
  );
};

export default GetRatesV2;
